import React from 'react';

import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';

export type TransferRouteType = 'static' | 'dynamic';

type TransferDestinationFieldProps = {
  routeType: TransferRouteType;
  onRouteTypeChange: (type: TransferRouteType) => void;
  /** Static mode: the E.164 destination number. Dynamic mode: routing instructions. */
  prompt: string;
  onPromptChange: (value: string) => void;
  error?: string;
};

export const TransferDestinationField = React.memo(function TransferDestinationField({
  routeType,
  onRouteTypeChange,
  prompt,
  onPromptChange,
  error,
}: TransferDestinationFieldProps) {
  return (
    <div className="space-y-3">
      <div>
        <Label className="mb-1 block">Transfer to</Label>
        <p className="text-sm text-muted-foreground">
          Choose whether the destination is fixed or resolved at runtime.
        </p>
      </div>

      <div className="flex w-fit items-center gap-0.5 rounded-lg border bg-muted p-0.5">
        <button
          type="button"
          onClick={() => onRouteTypeChange('static')}
          className={`rounded-md px-4 py-1.5 text-sm font-medium transition-colors ${
            routeType === 'static'
              ? 'bg-background text-foreground shadow-sm'
              : 'text-muted-foreground hover:text-foreground'
          }`}
        >
          Static
        </button>
        <button
          type="button"
          onClick={() => onRouteTypeChange('dynamic')}
          className={`rounded-md px-4 py-1.5 text-sm font-medium transition-colors ${
            routeType === 'dynamic'
              ? 'bg-background text-foreground shadow-sm'
              : 'text-muted-foreground hover:text-foreground'
          }`}
        >
          Dynamic
        </button>
      </div>

      {routeType === 'static' ? (
        <div className="space-y-2">
          <Input
            placeholder="e.g. +15551234567"
            value={prompt}
            onChange={event => onPromptChange(event.target.value)}
            aria-invalid={Boolean(error)}
          />
          <p className="text-xs text-muted-foreground">
            The call is always transferred to this fixed phone number, in E.164 format.
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          <Textarea
            placeholder="If the user wants to reach support, transfer to +1 (925) 222-2222; if the user wants to reach sales, transfer to +1 (925) 333-3333"
            value={prompt}
            onChange={event => onPromptChange(event.target.value)}
            className="min-h-[80px] resize-none"
            aria-invalid={Boolean(error)}
          />
          <p className="text-xs text-muted-foreground">
            The agent uses these routing instructions to resolve a destination at call time.
          </p>
        </div>
      )}

      {error ? <p className="text-xs text-destructive">{error}</p> : null}
    </div>
  );
});
